<template>
  <UpdateBanner />
  <router-view/>
</template>

<style lang="scss">
@import '~mdb-vue-ui-kit/src/scss/index.free.scss';

/* ============================================================
   Design tokens
   ============================================================ */
:root{
  /* Surfaces */
  --app-background:#f1f5f9;
  --surface:#ffffff;
  --surface-sunken:#f8fafc;
  --surface-inverse:#0f172a;

  /* Text */
  --text:#0f172a;
  --text-muted:#64748b;
  --text-subtle:#94a3b8;
  --text-inverse:#f8fafc;

  /* Lines */
  --border:#e2e8f0;
  --border-strong:#cbd5e1;

  /* Accents */
  --accent:#0f172a;
  --accent-hover:#1e293b;
  --focus:#2563eb;

  /* Status */
  --success:#16a34a;
  --success-soft:#f0fdf4;
  --success-border:#bbf7d0;
  --danger:#dc2626;
  --danger-soft:#fef2f2;
  --danger-border:#fecaca;

  /* Radii */
  --r-sm:8px;
  --r-md:12px;
  --r-lg:16px;
  --r-xl:22px;

  /* Elevation */
  --shadow-sm:0 1px 2px rgba(15,23,42,.06), 0 1px 3px rgba(15,23,42,.05);
  --shadow-md:0 2px 4px rgba(15,23,42,.04), 0 8px 20px rgba(15,23,42,.08);
  --shadow-lg:0 4px 8px rgba(15,23,42,.04), 0 20px 48px rgba(15,23,42,.12);

  /* Rhythm */
  --bar-height:64px;
}

*{box-sizing:border-box;}

html,body{
  margin:0;
  background-color:var(--app-background);
  color:var(--text);
  font-family:'Inter','Segoe UI',system-ui,-apple-system,sans-serif;
  -webkit-font-smoothing:antialiased;
  text-size-adjust:100%;
}

body{
  font-size:16px;
  overscroll-behavior:none;
}

/* Keep numeric data aligned and unambiguous */
.tnum{ font-variant-numeric:tabular-nums; }

/* ============================================================
   App shell
   ============================================================ */
#app{
  background-color:var(--app-background);
  min-height:100vh;
  min-height:100dvh;
  width:100%;
  /* Delay matches .pop-leave-active so the status tint arrives with the new
     panel rather than washing over the one that's still leaving. */
  transition:background-color .26s ease .16s;

  /* Status tinting driven by JS (#app.success / #app.error) */
  &.success{ background-color:var(--success-soft); }
  &.error{ background-color:var(--danger-soft); }
}

/* ============================================================
   Busy / disabled fieldset overlay
   ============================================================ */
fieldset{ border:0; margin:0; padding:0; min-width:0; }

fieldset[disabled]{
  position:relative;
}
fieldset[disabled] > .spinner-border:nth-of-type(1){
  z-index:100;
  margin-left:-1rem;
  margin-top:-1rem;
  color:var(--accent);
}
fieldset[disabled]::after{
  content:'';
  position:absolute;
  display:block;
  z-index:99;
  background-color:rgba(255,255,255,.72);
  backdrop-filter:blur(1px);
  border-radius:var(--r-md);
  inset:-6px;
}

/* ============================================================
   Buttons
   ============================================================ */
.eu-btn{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:.6rem;
  min-height:52px;
  padding:0 1.5rem;
  border:1px solid transparent;
  border-radius:var(--r-md);
  background:var(--accent);
  color:#fff;
  font-size:.95rem;
  font-weight:600;
  letter-spacing:.02em;
  line-height:1;
  cursor:pointer;
  text-decoration:none;
  transition:background-color .16s ease, box-shadow .16s ease, transform .08s ease, border-color .16s ease;
  box-shadow:var(--shadow-sm);

  &:hover{ background:var(--accent-hover); }
  &:active{ transform:translateY(1px); }
  &:focus-visible{
    outline:none;
    box-shadow:0 0 0 3px rgba(37,99,235,.35);
  }
  &[disabled]{ opacity:.55; cursor:not-allowed; }
}

.eu-btn--block{ width:100%; }

.eu-btn--secondary{
  background:var(--surface);
  color:var(--text);
  border-color:var(--border-strong);
  &:hover{ background:var(--surface-sunken); }
}

.eu-btn--ghost{
  background:transparent;
  color:var(--text-muted);
  border-color:transparent;
  box-shadow:none;
  &:hover{ background:rgba(15,23,42,.05); color:var(--text); }
}

.eu-btn--lg{
  min-height:60px;
  font-size:1.05rem;
}

/* ============================================================
   Cards & surfaces
   ============================================================ */
.eu-card{
  background:var(--surface);
  border:1px solid var(--border);
  border-radius:var(--r-lg);
  box-shadow:var(--shadow-md);
}

/* ============================================================
   MDB overrides — bring the kit in line with the tokens
   ============================================================ */
.modal-content{
  border:none;
  border-radius:var(--r-lg);
  box-shadow:var(--shadow-lg);
  overflow:hidden;
}
.modal-header{
  border-bottom:1px solid var(--border);
  padding:1.25rem 1.5rem;
  background:var(--surface);
}
.modal-title{
  font-size:.8rem;
  font-weight:700;
  letter-spacing:.1em;
  text-transform:uppercase;
  color:var(--text-muted);
}
.modal-body{ padding:1.5rem; }

.list-group-item{
  border-color:var(--border);
  padding:1rem 1.25rem;
  font-weight:500;
  cursor:pointer;
  transition:background-color .14s ease;
  &:first-child{ border-top-left-radius:var(--r-md); border-top-right-radius:var(--r-md); }
  &:last-child{ border-bottom-left-radius:var(--r-md); border-bottom-right-radius:var(--r-md); }
  &:hover{ background:var(--surface-sunken); }
  &.active{
    background:var(--accent);
    border-color:var(--accent);
    color:#fff;
  }
}

.progress{
  border-radius:999px;
  background:var(--surface-sunken);
  overflow:hidden;
}
.progress-bar{ background-color:var(--accent); }

/* MDB floating label input */
.form-outline .form-control{
  border-radius:var(--r-md);
  min-height:56px;
  font-size:1rem;
}
.form-outline .form-control ~ .form-notch div{
  border-color:var(--border-strong);
}
.form-outline .form-control:focus ~ .form-notch div{
  border-color:var(--focus);
  box-shadow:none;
}
.form-outline .form-control:focus ~ .form-label{ color:var(--focus); }

/* ============================================================
   Transitions
   ============================================================ */
.fade-enter-active,
.fade-leave-active{
  transition:opacity .32s ease;
}
.fade-enter-from,
.fade-leave-to{
  opacity:0;
}

.pop-enter-active{
  transition:opacity .24s ease, transform .24s cubic-bezier(.2,.9,.3,1.2);
}
.pop-leave-active{
  transition:opacity .16s ease, transform .16s ease;
}
.pop-enter-from,
.pop-leave-to{
  opacity:0;
  transform:translateY(10px) scale(.98);
}

@media (prefers-reduced-motion:reduce){
  *,*::before,*::after{
    animation-duration:.01ms !important;
    animation-iteration-count:1 !important;
    transition-duration:.01ms !important;
  }
}
</style>
